/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { CreateLfoChart, LfoHandleScales, LfoPoints } from "./lfoChart.ts";

export interface LfoAxisParam {
  value: number;
  min: number;
  max: number;
}

export interface LfoRateDepthCurveProps {
  rate: LfoAxisParam;
  depth: LfoAxisParam;
  onDrag?: (value: { rate: number; depth: number }) => void;
}

export function CreateLfoCurveChart(
  container: HTMLElement,
  mode: ChartMode,
  rateMin: number,
  rateMax: number,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const curves = LinesLayer();
  const handles = mode === "editor"
    ? HandlesLayer({ ...LfoHandleScales(rateMin, rateMax), onDrag })
    : null;
  const chart = CreateLfoChart(container, {
    mode,
    layers: [curves, ...(handles ? [handles] : [])],
  });
  return { chart, curves, handles, destroy: chart.destroy };
}

export function LfoRateDepthCurve(props: LfoRateDepthCurveProps) {
  const mode: ChartMode = props.onDrag ? "editor" : "inline";
  const onDrag = useLatest(props.onDrag);
  const { min, max } = props.rate;
  const { ref, chart } = useChart(
    (container) =>
      CreateLfoCurveChart(container, mode, min, max, (_, p) =>
        onDrag.current?.({
          rate: parseFloat(p.x.toFixed(2)),
          depth: parseFloat(p.y.toFixed(3)),
        })),
    [mode, min, max],
  );

  const rate = props.rate.value;
  const depth = props.depth.value;

  useEffect(() => {
    if (!chart) return;
    chart.curves.set([{ id: "lfo", points: LfoPoints(rate, depth, min, max) }]);
    chart.handles?.set([
      {
        id: "rate",
        x: rate,
        y: depth,
        label: `${rate.toFixed(1)}Hz`,
        sublabel: `${(depth * 100).toFixed(0)}%`,
      },
    ]);
    chart.chart.render();
  }, [chart, rate, depth]);

  return <div ref={ref} />;
}
